/* eslint jsx-a11y/anchor-is-valid: 0 */

import React, {useState, useEffect, useRef} from "react";
import {useParams} from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  FormSelect,
} from "shards-react";
import {loadConnectAndInitialize} from "@stripe/connect-js";
import {ConnectComponentsProvider, ConnectPayments, ConnectPayouts} from "@stripe/react-connect-js";
import PageTitle from "../../components/common/PageTitle";
import Back from "../../components/common/Back";
import {accessCheck} from "../../utils/tools";
import {STRIPE_PUBLISHABLE_KEY} from "../../environmentVariables";

const StripePayments = (props) => {

  let {locationId} = useParams();
  const [location, setLocation] = useState(props.locations.find(location => location.id === locationId));
  const [activeTab, setActiveTab] = useState('payments');
  const [connectInstance, setConnectInstance] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const [dashboardAccess, setDashboardAccess] = useState(null);

  // Stripe re-invokes fetchClientSecret on every session refresh, so it must
  // read the location through a ref to avoid a stale closure (same pattern as
  // the onboarding flow in EditLocation).
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location }, [location]);

  useEffect(() => {
    document.title = 'Payments dashboard: EnforcePlus admin';
    // The backend callable enforces this too (super, or admin of the same
    // client) — this is just to fail fast with a redirect instead of an error.
    if (!accessCheck(props.user, 'payments') || (props.user.type !== 'super' && props.user.type !== 'admin')) {
      window.location.href = '/';
    }
  }, [])

  useEffect(() => {
    setLocation(props.locations?.find(location => location.id === locationId))
  }, [props.locations])

  const stripeReady = location?.payment_processor === 'stripe' && location?.stripe_account_status === 'active';

  // Picker mode: reached from the sidebar (/payments-stripe, no :locationId).
  // With exactly one eligible location, skip the picker entirely — that's the
  // common case for an operator admin.
  const eligibleLocations = (props.locations || [])
    .filter(loc => loc.payment_processor === 'stripe' && loc.stripe_account_status === 'active')
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (!locationId && props.loadedLocations && eligibleLocations.length === 1) {
      window.location.href = '/location/' + eligibleLocations[0].id + '/payments-stripe'
    }
  }, [locationId, props.loadedLocations, props.locations])

  useEffect(() => {
    if (!stripeReady || connectInstance) {
      return
    }
    if (!STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY.includes('REPLACE_ME')) {
      setConnectError('Stripe is not configured for this environment.')
      return
    }
    try {
      const instance = loadConnectAndInitialize({
        publishableKey: STRIPE_PUBLISHABLE_KEY,
        fetchClientSecret: async () => {
          try {
            const result = await props.createStripeDashboardSession(locationRef.current.client, locationRef.current.id)
            setDashboardAccess(result.access)
            return result.clientSecret
          } catch (err) {
            // Surfaced from here or the failure vanishes silently into the
            // Connect JS internals and the embed just never renders.
            setConnectError(err.message || 'Failed to load the payments dashboard')
            throw err
          }
        },
      })
      setConnectInstance(instance)
    } catch (err) {
      setConnectError(err.message || 'Failed to load the payments dashboard')
    }
  }, [stripeReady])

  const renderStatusMessage = () => {
    if (!location) {
      // props.locations is a fully-loaded array — once loadedLocations is
      // true, a missing id will never resolve, so don't pretend to load.
      return props.loadedLocations ? (
        <div className="alert alert-warning">Location not found, or you don't have access to it.</div>
      ) : (
        <div className="alert alert-info">Loading location…</div>
      )
    }
    if (location.payment_processor !== 'stripe') {
      return (
        <div className="alert alert-warning">
          This location doesn't use Stripe as its payment processor, so there is no Stripe payments dashboard for it.
        </div>
      )
    }
    if (location.stripe_account_status === 'pending_review') {
      return (
        <div className="alert alert-info">
          The Stripe account for this location is still being reviewed by Stripe. The payments dashboard will become available once the account is verified.
        </div>
      )
    }
    return (
      <div className="alert alert-info">
        The Stripe account for this location hasn't completed onboarding yet. The payments dashboard will become available once the account is verified.
      </div>
    )
  }

  return (
    <Container fluid className="main-content-container px-4">
      <Row>
        <Col>
          <Row noGutters className="page-header py-4">
            <PageTitle xs="12" sm="6" title={"Payments dashboard" + (location?.name ? ": " + location.name : "")} className="text-sm-left mb-3" />
          </Row>
          <Row>
            <Col>
              <Back />
            </Col>
          </Row>
          <Card small className="mb-4 mt-4">
            <CardBody>
              {!locationId ? (
                !props.loadedLocations ? (
                  <div className="alert alert-info">Loading locations…</div>
                ) : eligibleLocations.length === 0 ? (
                  <div className="alert alert-warning">
                    None of your locations has an active Stripe account yet, so there is no payments dashboard to show.
                  </div>
                ) : (
                  <div style={{maxWidth: 420}}>
                    <label htmlFor="feStripeDashboardLocation" className="form-label-custom">Choose a location</label>
                    <FormSelect
                      id="feStripeDashboardLocation"
                      defaultValue=""
                      onChange={event => event.target.value && (window.location.href = '/location/' + event.target.value + '/payments-stripe')}
                    >
                      <option value="">Select</option>
                      {eligibleLocations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </FormSelect>
                  </div>
                )
              ) : (
              <>
              {connectError && (
                <div className="alert alert-danger">{connectError}</div>
              )}
              {!stripeReady ? (
                renderStatusMessage()
              ) : (
                <>
                  <Row className="mb-3">
                    <Col>
                      <Button
                        theme={activeTab === 'payments' ? 'accent' : 'outline-secondary'}
                        className="mr-2"
                        onClick={() => setActiveTab('payments')}
                      >Payments</Button>
                      <Button
                        theme={activeTab === 'payouts' ? 'accent' : 'outline-secondary'}
                        onClick={() => setActiveTab('payouts')}
                      >Payouts &amp; balance</Button>
                    </Col>
                    {dashboardAccess && (
                      <Col className="text-right d-flex align-items-center justify-content-end">
                        <span className="text-muted">
                          {dashboardAccess === 'full' ? 'Refunds and dispute responses enabled' : 'Read-only'}
                        </span>
                      </Col>
                    )}
                  </Row>
                  {connectInstance && (
                    <ConnectComponentsProvider connectInstance={connectInstance}>
                      {/* Both stay mounted — remounting on every tab switch would
                          tear down and rebuild the Stripe iframe each time. */}
                      <div style={{display: activeTab === 'payments' ? 'block' : 'none'}}>
                        <ConnectPayments />
                      </div>
                      <div style={{display: activeTab === 'payouts' ? 'block' : 'none'}}>
                        <ConnectPayouts />
                      </div>
                    </ConnectComponentsProvider>
                  )}
                </>
              )}
              </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default StripePayments;
